import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Tabs, Tab } from "react-bootstrap";
import { Link } from "react-router-dom";

import HistoricalDataGraph from "./HistoricalDataGraph";
import ErrorStatsCards from "./ErrorStatsCards";
import LatestOperationsHistoryTable from "./LatestOperationsHistoryTable";
import ComparisonBadge from "./ComparisonBadge";
import QuickDurationPrediction from "./QuickDurationPrediction";

import type { ModelData } from "@/lib/ModelDetails";

import "./ModelDetails.scss";

type ModelInfoField = {
    label: string;
    value: string;
    getLink?: () => string;
};

type Props = {
    model: ModelData;
    activeTab: string;
    setActiveTab: (tab: string | null) => void;
};

/**
 * Displays the model details using the model data.
 * Allows switching between different tabs to view the model properties, latest operations, and raw model data.
 */
export default function ModelDetails({ model, activeTab, setActiveTab }: Props) {
    const { t } = useLingui();
    const [breakTimeMin, setBreakTimeMin] = useState(0);

    const modelInfoFields: ModelInfoField[] = [
        {
            label: t`Material ID` + ":",
            value: model.materialId,
            getLink: () => `/digital-twin/resources/materials/${model.materialUuid}`,
        },
        {
            label: t`Line ID` + ":",
            value: model.lineId,
            getLink: () => `/digital-twin/resources/lines/${model.lineUuid}`,
        },
        {
            label: t`Line Title` + ":",
            value: model.lineTitle,
            getLink: () => `/digital-twin/resources/lines/${model.lineUuid}`,
        },
        {
            label: t`Process Num` + ":",
            value: model.processNum,
        },
    ];

    const fallbacks = [
        {
            label: t`Ignore models for this material` + ":",
            value: model.ignorePredictionModelForMaterial ? t`Yes` : t`No`,
            badge: model.ignorePredictionModelForMaterial ? "danger" : "info",
        },
        {
            label: t`Ignore models for this line` + ":",
            value: model.ignorePredictionModelForLine ? t`Yes` : t`No`,
            badge: model.ignorePredictionModelForLine ? "danger" : "info",
        },
        {
            label: t`Fallback if too optimistic` + ":",
            value: model.skipTooOptimisticFallbackToNormative ? t`Yes` : t`No`,
            badge: model.skipTooOptimisticFallbackToNormative ? "danger" : "info",
        },
        {
            label: t`Minimal machine-time for fallback` + ":",
            value: `${+model.modelFallbackThresholdH} ${t`h`}`, // + is to convert to number, otherwise 0 becomes undefined
        },
    ];

    return (
        <>
            <main className="model-details">
                <div className="content-header d-flex flex-column gap-2 mt-1 mb-1">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex justify-content-between width-100 gap-3">
                            <h1 className="content-title">{model.materialTitle}</h1>
                            <div className="d-flex align-items-start gap-2">
                                {model.isDisabled ? (
                                    <span className="tag variant-danger">{t`Model Disabled`}</span>
                                ) : (
                                    <span className="tag variant-success">{t`Model Active`}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <table className="data-fields d-flex gap-2">
                        <tbody>
                            {modelInfoFields.map(({ label, value, getLink }, index) => (
                                <tr key={index}>
                                    <th>{label}</th>
                                    <td>
                                        {getLink ? (
                                            <Link to={getLink()} target="_blank">
                                                {value}
                                            </Link>
                                        ) : (
                                            value
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="d-flex flex-column gap-3 tabs-container mt-3">
                        <Tabs id="modal-tabs2" activeKey={activeTab} onSelect={setActiveTab} variant="tabs">
                            <Tab eventKey="modelProperties" title={t`Model Properties & Comparison`}>
                                <div id="propertiesTab" className="d-flex flex-column gap-3">
                                    <div className="d-flex flex-wrap gap-3 equal-columns">
                                        <div className="d-flex flex-column gap-3">
                                            <ComparisonBadge
                                                title={t`Production Speed`}
                                                modelValue={model.modelSpeed}
                                                normativeValue={model.normativeSpeed}
                                                unit={t`Parts/hour`}
                                                higherIsBetter={true}
                                            />
                                            <ComparisonBadge
                                                title={t`Tool Setup Time`}
                                                modelValue={model.modelSetup}
                                                normativeValue={model.normativeSetup}
                                                unit={t`Hours`}
                                                higherIsBetter={false}
                                            />

                                            <div className="card p-0">
                                                <div className="card-title m-3">{t`Fallbacks`}</div>
                                                {fallbacks.map(({ label, value, badge }, index) => (
                                                    <div className="card-row card-row-border-bottom" key={index}>
                                                        <span className="card-label">{label}</span>
                                                        <span
                                                            className={
                                                                badge
                                                                    ? `card-badge card-badge-${badge}`
                                                                    : "card-value card-value--small"
                                                            }
                                                        >
                                                            {value}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <QuickDurationPrediction
                                                model={model}
                                                breakTimeMin={breakTimeMin}
                                                setBreakTimeMin={setBreakTimeMin}
                                            />
                                        </div>
                                    </div>

                                    <div className="card p-0">
                                        <div className="card-title card-title-border-bottom p-3">
                                            {t`Historical Performance Scatter`}
                                        </div>
                                        <div className="d-flex justify-content-center m-3">
                                            <HistoricalDataGraph model={model} />
                                        </div>
                                    </div>
                                </div>
                            </Tab>
                            <Tab
                                eventKey="latestOperations"
                                title={t`Latest Work-Orders (${model.latestOperations.length})`}
                            >
                                <ErrorStatsCards model={model} breakTimeMin={breakTimeMin} />
                                <LatestOperationsHistoryTable model={model} breakTimeMin={breakTimeMin} />
                            </Tab>
                            <Tab eventKey="rawModelData" title={t`Raw Model Data (JSON)`}>
                                <textarea
                                    className="raw-data text-monospace p-2"
                                    rows={50}
                                    defaultValue={JSON.stringify(model, null, 2)}
                                />
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            </main>
        </>
    );
}
